import { useState } from 'react';
import { CardElement } from '@stripe/react-stripe-js';
import type { Stripe, StripeElements } from '@stripe/stripe-js';

import type { PatientOrderingPaymentIntent } from '@customTypes/test-ordering';

import type { FormType } from '../types/Wizard.types';

interface UseWizardPaymentProps {
  paymentIntent: PatientOrderingPaymentIntent | undefined;
  values: FormType;
  updateValue: (path: string, val: unknown) => void;
  stripe: Stripe | null;
  elements: StripeElements | null;
}

interface UseWizardPaymentReturn {
  handlePaymentConfirmation: () => Promise<boolean>;
  stripeError: string | undefined;
  loadingPayment: boolean;
}

/**
 * Hook that manages payment confirmation and Stripe integration.
 * Handles card confirmation and billing details.
 */
export function useWizardPayment({
  paymentIntent,
  values,
  updateValue,
  stripe,
  elements,
}: UseWizardPaymentProps): UseWizardPaymentReturn {
  const [stripeError, setStripeError] = useState<string | undefined>(undefined);
  const [loadingPayment, setLoadingPayment] = useState(false);

  const handlePaymentConfirmation = async (): Promise<boolean> => {
    // Helper to get current billing address for storing after confirmation
    const getCurrentBillingAddress = () => {
      if (values.useShippingForBilling && values.address) {
        return {
          useShippingForBilling: true,
          address: {
            street: values.address.street,
            street2: values.address.street2,
            city: values.address.city,
            state: values.address.state,
            zip: values.address.zip,
          },
        };
      } else if (values.billingAddress) {
        return {
          useShippingForBilling: false,
          address: {
            street: values.billingAddress.street,
            street2: values.billingAddress.street2,
            city: values.billingAddress.city,
            state: values.billingAddress.state,
            zip: values.billingAddress.zip,
          },
        };
      }
      return null;
    };

    // If payment is already confirmed, allow continue
    if (values.paymentIntentId) {
      return true;
    }

    // Guard against double confirmation
    if (loadingPayment) {
      return false; // Already processing
    }

    // Validate payment_info exists with client_secret
    if (!paymentIntent?.client_secret) {
      setStripeError('Payment information is not available. Please refresh the page and try again.');
      return false;
    }

    // Validate stripe and elements are ready
    if (!stripe || !elements) {
      setStripeError('Payment system is not ready. Please refresh the page and try again.');
      return false;
    }

    // Get card element
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setStripeError('Card information is required.');
      return false;
    }

    // Prepare billing details
    let billingDetails: Record<string, unknown> | undefined;
    if (values.useShippingForBilling && values.address) {
      billingDetails = {
        address: {
          line1: values.address.street,
          line2: values.address.street2,
          city: values.address.city,
          state: values.address.state,
          postal_code: values.address.zip,
          country: 'US',
        },
      };
    } else if (values.billingAddress) {
      billingDetails = {
        address: {
          line1: values.billingAddress.street,
          line2: values.billingAddress.street2,
          city: values.billingAddress.city,
          state: values.billingAddress.state,
          postal_code: values.billingAddress.zip,
          country: 'US',
        },
      };
    }

    // Set loading state for confirmation (but PaymentStep won't unmount CardElement)
    setLoadingPayment(true);
    setStripeError(undefined);

    try {
      // Confirm payment - CardElement must remain mounted during this call
      const paymentIntentResponse = await stripe.confirmCardPayment(paymentIntent.client_secret, {
        payment_method: {
          card: cardElement,
          ...(billingDetails ? { billing_details: billingDetails } : {}),
        },
      });

      if (paymentIntentResponse.error) {
        setStripeError(paymentIntentResponse.error.message || 'Payment failed. Please try again.');
        setLoadingPayment(false);
        return false;
      }

      // Store payment intent ID and the billing address used during confirmation
      updateValue('paymentIntentId', paymentIntent.id);
      updateValue('confirmedBillingAddress', getCurrentBillingAddress());
      setStripeError(undefined);
      setLoadingPayment(false);
      return true;
    } catch (error) {
      setStripeError(error instanceof Error ? error.message : 'An error occurred during payment. Please try again.');
      setLoadingPayment(false);
      return false;
    }
  };

  return {
    handlePaymentConfirmation,
    stripeError,
    loadingPayment,
  };
}
